import { Routes, Route, useLocation, Navigate } from 'react-router'
import PageTransition from '@/components/PageTransition'
import SkipLink from '@/components/SkipLink'
import React, { type ReactNode, Component, useEffect, lazy, Suspense, useState } from 'react'
import Home from './pages/Home'
import LoginModal from './sections/LoginModal'
import { ErrorBoundary } from './components/ErrorBoundary'
import SupportModal from './components/SupportModal'
import KeyboardShortcutsHelp from './components/KeyboardShortcutsHelp'
import HelpCenter from './components/HelpCenter'
// Lazy-loaded public pages — not needed for initial homepage render
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const LegalCompliance = lazy(() => import('./pages/LegalCompliance'))
const Accessibility = lazy(() => import('./pages/Accessibility'))
const About = lazy(() => import('./pages/About'))
const Blog = lazy(() => import('./pages/Blog'))
const GoogleCallback = lazy(() => import('./pages/GoogleCallback'))
const AppleCallback = lazy(() => import('./pages/AppleCallback'))
const MagicCallback = lazy(() => import('./pages/MagicCallback'))
const Integrations = lazy(() => import('./pages/Integrations'))
const Changelog = lazy(() => import('./pages/Changelog'))
const CaseStudies = lazy(() => import('./pages/CaseStudies'))
const Contact = lazy(() => import('./pages/Contact'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const Coverage = lazy(() => import('./pages/Coverage'))
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'))
const Support = lazy(() => import('./pages/Support'))
const Login = lazy(() => import('./pages/Login'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'))
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmail'))
const StatusPage = lazy(() => import('./pages/StatusPage'))
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'))
const NotFound = lazy(() => import('./pages/NotFound'))
// Authenticated pages — lazy loaded
const Settings = lazy(() => import('./pages/Settings'))
const Account = lazy(() => import('./pages/Account'))
const MyPlan = lazy(() => import('./pages/MyPlan'))
const SearchHistory = lazy(() => import('./pages/SearchHistory'))
import AdminGuard from './components/AdminGuard'
// Admin pages — lazy loaded (not needed for public homepage)
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'))
const AdminOverview = lazy(() => import('./pages/admin/Overview'))
const AdminUsers = lazy(() => import('./pages/admin/Users'))
const AdminTickets = lazy(() => import('./pages/admin/Tickets'))
const CoverageDemand = lazy(() => import('./pages/admin/CoverageDemand'))
const CoverageExpansion = lazy(() => import('./pages/admin/CoverageExpansion'))
const AgentOperations = lazy(() => import('./pages/admin/AgentOperations'))
// Staged opportunity pages — admin-only preview
const OpportunityHome = lazy(() => import('./pages/opportunities/OpportunityHome'))
const OpportunityArea = lazy(() => import('./pages/opportunities/OpportunityArea'))
import { ContactModalProvider } from './hooks/useContactModal'
import { LoginModalProvider, useLoginModal } from './hooks/useLoginModal'
import { SupportModalProvider, useSupportModal } from './hooks/useSupportModal'
import { useGlobalShortcuts } from './hooks/useKeyboardShortcuts'
import { useWebVitals } from './hooks/usePerformance'
import { useServiceWorker } from './hooks/useServiceWorker'
import SkipToContent from './components/SkipToContent'
import ExitIntentModal from './components/ExitIntentModal'
import { usePageTracking } from './hooks/usePageTracking'

const CustomerDashboard = lazy(() => import('./pages/Dashboard'));

function LoginModalGlobal() {
  const { isOpen, close } = useLoginModal();
  return <LoginModal isOpen={isOpen} onClose={close} />;
}

function SupportModalGlobal() {
  const { isOpen, close } = useSupportModal();
  return <SupportModal isOpen={isOpen} onClose={close} />;
}

function DeferredGlobalWork() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Defer non-LCP-critical work until after the page is interactive
    // This gives the browser breathing room to paint the hero/LCP
    const id = requestIdleCallback
      ? requestIdleCallback(() => setReady(true), { timeout: 2000 })
      : setTimeout(() => setReady(true), 1500);
    return () => {
      if (typeof id === 'number') clearTimeout(id);
      else if ('didTimeout' in id) cancelIdleCallback(id);
    };
  }, []);

  if (!ready) return null;

  return (
    <>
      <ExitIntentModal />
      <GlobalShortcuts />
    </>
  );
}

function GlobalShortcuts() {
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useGlobalShortcuts();

  // Register ? shortcut for help
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      // Don't trigger when typing in inputs
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setShowShortcuts((s) => !s);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setShowHelp((h) => !h);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <>
      <KeyboardShortcutsHelp isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
      <HelpCenter isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </>
  );
}

function WebVitalsTracker() {
  useWebVitals();
  return null;
}

function PageTracker() {
  usePageTracking();
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Lightweight route-level loading fallback — preserves shell, minimal JS */
function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center" style={{ background: '#07110D' }}>
      <div className="text-center">
        <div
          className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2"
          style={{ borderColor: 'rgba(247,248,244,0.12)', borderTopColor: '#F7F8F4' }}
        />
        <p className="font-body text-sm" style={{ color: '#A8B8AE' }}>Loading...</p>
      </div>
    </div>
  );
}

/** Dashboard uses a full-screen dark fallback */
function DashboardFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center" style={{ background: '#1A2332' }}>
      <div className="text-center">
        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2" style={{ borderColor: 'rgba(247,248,244,0.12)', borderTopColor: '#F7F8F4' }} />
        <p className="font-body text-sm" style={{ color: '#A8B8AE' }}>Loading dashboard...</p>
      </div>
    </div>
  );
}

/** Route-level error boundary wrapper — isolates crashes to individual routes */
class RouteErrorBoundaryInner extends Component<{ children: ReactNode }> {
  state = { hasError: false, error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { hasError: true, error }; }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('RouteErrorBoundary caught:', error, errorInfo);
    this.setState({ error });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center px-6" style={{ background: '#0F1923' }}>
          <div className="w-full max-w-md rounded-2xl p-8 text-center" style={{ background: 'rgba(245,245,243,0.05)', border: '1px solid rgba(247,248,244,0.12)' }}>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full mx-auto" style={{ background: 'rgba(239,68,68,0.15)' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF445F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <h2 className="font-display mb-2 text-lg font-medium" style={{ color: '#F7F8F4' }}>Something went wrong</h2>
            <p className="font-body mb-2 text-sm" style={{ color: '#A8B8AE' }}>
              This section encountered an error. The rest of the app is still working.
            </p>
            {this.state.error && (
              <pre className="text-left text-xs text-red-400 mb-4 overflow-auto max-h-40 p-2 rounded" style={{ background: 'rgba(239,68,68,0.1)', fontFamily: 'monospace' }}>
                {this.state.error.message}
                {this.state.error.stack}
              </pre>
            )}

            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-full px-6 py-3 text-sm font-medium transition-all hover:opacity-90"
              style={{ background: '#F7F8F4', color: '#07110D' }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  return <RouteErrorBoundaryInner>{children}</RouteErrorBoundaryInner>;
}

export default function App() {
  useServiceWorker(); // Register service worker for offline support
  return (
    <>
      <SkipToContent />
      <DeferredGlobalWork />
      <WebVitalsTracker />
      <PageTracker />
      <ErrorBoundary
      fallback={
        <div className="flex min-h-screen flex-col items-center justify-center px-6" style={{ background: '#07110D' }}>
          <div className="w-full max-w-md rounded-2xl p-8 text-center" style={{ background: 'rgba(245,245,243,0.05)', border: '1px solid rgba(247,248,244,0.12)' }}>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full mx-auto" style={{ background: 'rgba(239,68,68,0.15)' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF445F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <h2 className="font-display mb-2 text-lg font-medium" style={{ color: '#F7F8F4' }}>Something went wrong</h2>
            <p className="font-body mb-6 text-sm" style={{ color: '#A8B8AE' }}>
              We encountered an unexpected error. Please refresh the page or try again later.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-full px-6 py-3 text-sm font-medium transition-all hover:opacity-90"
              style={{ background: '#F7F8F4', color: '#07110D' }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      }
    >
      <ContactModalProvider>
        <LoginModalProvider>
          <SupportModalProvider>
            <LoginModalGlobal />
            <SupportModalGlobal />
            <ScrollToTop />
            <PageTransition>
              <SkipLink />
              <main id="main-content">
                <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/privacy" element={<Suspense fallback={<RouteFallback />}><Privacy /></Suspense>} />
                <Route path="/terms" element={<Suspense fallback={<RouteFallback />}><Terms /></Suspense>} />
                <Route path="/legal-compliance" element={<Suspense fallback={<RouteFallback />}><LegalCompliance /></Suspense>} />
                <Route path="/accessibility" element={<Suspense fallback={<RouteFallback />}><Accessibility /></Suspense>} />
                <Route path="/about" element={<Suspense fallback={<RouteFallback />}><About /></Suspense>} />
                <Route path="/blog" element={<Suspense fallback={<RouteFallback />}><Blog /></Suspense>} />
                <Route path="/auth/google/callback" element={<Suspense fallback={<RouteFallback />}><GoogleCallback /></Suspense>} />
                <Route path="/auth/apple/callback" element={<Suspense fallback={<RouteFallback />}><AppleCallback /></Suspense>} />
                <Route path="/auth/magic" element={<Suspense fallback={<RouteFallback />}><MagicCallback /></Suspense>} />
                <Route path="/integrations" element={<Suspense fallback={<RouteFallback />}><Integrations /></Suspense>} />
                <Route path="/changelog" element={<Suspense fallback={<RouteFallback />}><Changelog /></Suspense>} />
                <Route path="/case-studies" element={<Suspense fallback={<RouteFallback />}><CaseStudies /></Suspense>} />
                <Route path="/contact" element={<Suspense fallback={<RouteFallback />}><Contact /></Suspense>} />
                <Route path="/pricing" element={<Suspense fallback={<RouteFallback />}><PricingPage /></Suspense>} />
                <Route path="/coverage" element={<Suspense fallback={<RouteFallback />}><Coverage /></Suspense>} />
                <Route path="/status" element={<Suspense fallback={<RouteFallback />}><StatusPage /></Suspense>} />
                <Route path="/payment-success" element={<Suspense fallback={<RouteFallback />}><PaymentSuccess /></Suspense>} />
                <Route path="/login" element={<Suspense fallback={<RouteFallback />}><Login /></Suspense>} />
                <Route path="/register" element={<Suspense fallback={<RouteFallback />}><Login initialMode="signup" /></Suspense>} />
                <Route path="/signup" element={<Navigate to="/register" replace />} />
                <Route path="/forgot-password" element={<Suspense fallback={<RouteFallback />}><ForgotPasswordPage /></Suspense>} />
                <Route path="/reset-password" element={<Suspense fallback={<RouteFallback />}><ResetPasswordPage /></Suspense>} />
                <Route path="/verify-email" element={<Suspense fallback={<RouteFallback />}><VerifyEmailPage /></Suspense>} />
                <Route path="/support" element={<Suspense fallback={<RouteFallback />}><Support /></Suspense>} />
                <Route path="/cookie-policy" element={<Suspense fallback={<RouteFallback />}><CookiePolicy /></Suspense>} />
                <Route path="/notifications" element={<Navigate to="/account" replace />} />
                <Route path="/admin" element={
                  <Suspense fallback={<DashboardFallback />}>
                    <AdminGuard>
                      <AdminLayout>
                        <AdminOverview />
                      </AdminLayout>
                    </AdminGuard>
                  </Suspense>
                } />
                <Route path="/admin/users" element={
                  <Suspense fallback={<DashboardFallback />}>
                    <AdminGuard>
                      <AdminLayout>
                        <AdminUsers />
                      </AdminLayout>
                    </AdminGuard>
                  </Suspense>
                } />
                <Route path="/admin/tickets" element={
                  <Suspense fallback={<DashboardFallback />}>
                    <AdminGuard>
                      <AdminLayout>
                        <AdminTickets />
                      </AdminLayout>
                    </AdminGuard>
                  </Suspense>
                } />
                <Route path="/admin/coverage-demand" element={
                  <Suspense fallback={<DashboardFallback />}>
                    <AdminGuard>
                      <AdminLayout>
                        <CoverageDemand />
                      </AdminLayout>
                    </AdminGuard>
                  </Suspense>
                } />
                <Route path="/admin/coverage-expansion" element={
                  <Suspense fallback={<DashboardFallback />}>
                    <AdminGuard>
                      <AdminLayout>
                        <CoverageExpansion />
                      </AdminLayout>
                    </AdminGuard>
                  </Suspense>
                } />
                <Route path="/admin/agent-operations" element={
                  <Suspense fallback={<DashboardFallback />}>
                    <AdminGuard>
                      <AdminLayout>
                        <AgentOperations />
                      </AdminLayout>
                    </AdminGuard>
                  </Suspense>
                } />
                <Route path="/dashboard" element={<Suspense fallback={<DashboardFallback />}><RouteErrorBoundary><CustomerDashboard /></RouteErrorBoundary></Suspense>} />
                <Route path="/settings" element={<Suspense fallback={<DashboardFallback />}><Settings /></Suspense>} />
                <Route path="/account" element={<Suspense fallback={<DashboardFallback />}><Account /></Suspense>} />
                <Route path="/my-plan" element={<Suspense fallback={<DashboardFallback />}><MyPlan /></Suspense>} />
                <Route path="/reports" element={<Navigate to="/dashboard" replace />} />
                <Route path="/search" element={<Navigate to="/dashboard" replace />} />
                <Route path="/search-history" element={<Suspense fallback={<DashboardFallback />}><SearchHistory /></Suspense>} />
                {/* Staged opportunity routes - admin-only preview */}
                <Route path="/opportunities" element={
                  <Suspense fallback={<DashboardFallback />}>
                    <AdminGuard>
                      <OpportunityHome />
                    </AdminGuard>
                  </Suspense>
                } />
                <Route path="/opportunities/:areaId" element={
                  <Suspense fallback={<DashboardFallback />}>
                    <AdminGuard>
                      <OpportunityArea />
                    </AdminGuard>
                  </Suspense>
                } />
                <Route path="*" element={<Suspense fallback={<RouteFallback />}><NotFound /></Suspense>} />
              </Routes>
              </main>
            </PageTransition>
          </SupportModalProvider>
        </LoginModalProvider>
      </ContactModalProvider>
    </ErrorBoundary>
    </>
  )
}
